// Contract Obligations Service
// Manages creation, tracking, monitoring, and notifications for contract obligations

export const OBLIGATION_CATEGORIES = [
  "Delivery",
  "Payment",
  "Compliance",
  "Reporting",
  "Service Level",
  "Confidentiality",
  "Audit",
  "Other",
];

export const OBLIGATION_STATUSES = [
  "Not Started",
  "In Progress",
  "On Track",
  "At Risk",
  "Completed",
  "Overdue",
  "Waived",
];

export const OBLIGATION_PRIORITIES = ["Critical", "High", "Medium", "Low"];
export const RISK_LEVELS = ["Low", "Medium", "High", "Critical"];

// Create a new obligation
export const createObligation = async (service, contractId, obligationData) => {
  try {
    const payload = {
      Title: obligationData.title,
      ContractID: contractId,
      ContractRefNo: obligationData.contractRefNo,
      Description: obligationData.description,
      Category: obligationData.category,
      ResponsibleParty: obligationData.responsibleParty,
      ResponsibleEmail: obligationData.responsibleEmail,
      DueDate: obligationData.dueDate,
      Priority: obligationData.priority,
      Status: obligationData.status || "Not Started",
      EstimatedDays: obligationData.estimatedDays || 0,
      ActualDays: 0,
      ProgressPercentage: 0,
      RiskLevel: obligationData.riskLevel || "Medium",
      RiskDescription: obligationData.riskDescription || "",
      StakesDescription: obligationData.stakesDescription || "",
      Dependencies: obligationData.dependencies || "",
      ContingentOn: obligationData.contingentOn || "",
      Notes: obligationData.notes || "",
      CreatedBy: obligationData.createdBy || "System",
      CreatedAt: new Date().toISOString(),
      RemindersEnabled: obligationData.remindersEnabled !== false,
      ReminderDays: obligationData.reminderDays || 7,
      EscalationEnabled: obligationData.escalationEnabled !== false,
      EscalationDays: obligationData.escalationDays || 3,
      MilestoneStatus: JSON.stringify(obligationData.milestones || []),
    };

    const result = await service.createListItem("ContractObligations", payload);
    return { ...result.fields, id: result.id };
  } catch (e) {
    console.error("Error creating obligation:", e);
    throw e;
  }
};

// Update an obligation
export const updateObligation = async (service, obligationId, updates) => {
  try {
    const payload = {
      ...updates,
      MilestoneStatus: typeof updates.MilestoneStatus === "string"
        ? updates.MilestoneStatus
        : JSON.stringify(updates.MilestoneStatus || []),
    };

    await service.updateListItem("ContractObligations", obligationId, payload);
    return true;
  } catch (e) {
    console.error("Error updating obligation:", e);
    throw e;
  }
};

// Get all obligations for a contract
export const getContractObligations = async (service, contractId) => {
  try {
    const result = await service.getListItems("ContractObligations");
    if (!result?.value) return [];

    return (result.value || [])
      .filter(item => item.fields.ContractID === contractId)
      .map(item => ({
        ...item.fields,
        id: item.id,
        milestones: tryParseJson(item.fields.MilestoneStatus, []),
      }))
      .sort((a, b) => new Date(a.DueDate || 0) - new Date(b.DueDate || 0));
  } catch (e) {
    console.error("Error fetching obligations:", e);
    return [];
  }
};

// Get all obligations across all contracts
export const getAllObligations = async (service) => {
  try {
    const result = await service.getListItems("ContractObligations");
    if (!result?.value) return [];

    return (result.value || []).map(item => ({
      ...item.fields,
      id: item.id,
      milestones: tryParseJson(item.fields.MilestoneStatus, []),
    }));
  } catch (e) {
    console.error("Error fetching all obligations:", e);
    return [];
  }
};

// Mark obligation as completed
export const completeObligation = async (service, obligationId) => {
  try {
    await service.updateListItem("ContractObligations", obligationId, {
      Status: "Completed",
      ProgressPercentage: 100,
      CompletionDate: new Date().toISOString(),
    });
    return true;
  } catch (e) {
    console.error("Error completing obligation:", e);
    throw e;
  }
};

// Update milestone status
export const updateMilestones = async (service, obligationId, milestones) => {
  try {
    const percentage = calculateProgressPercentage(milestones);
    await service.updateListItem("ContractObligations", obligationId, {
      MilestoneStatus: JSON.stringify(milestones),
      ProgressPercentage: percentage,
      Status: percentage === 100 ? "Completed" : "In Progress",
    });
    return true;
  } catch (e) {
    console.error("Error updating milestones:", e);
    throw e;
  }
};

// Calculate progress percentage from milestones
export const calculateProgressPercentage = (milestones) => {
  if (!milestones || milestones.length === 0) return 0;
  const completed = milestones.filter(m => m.completed).length;
  return Math.round((completed / milestones.length) * 100);
};

// Calculate obligation risk score
export const calculateRiskScore = (obligation) => {
  const today = new Date();
  const dueDate = new Date(obligation.DueDate);
  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
  const progress = obligation.ProgressPercentage || 0;
  const expectedProgress =
    obligation.EstimatedDays && daysUntilDue > 0
      ? Math.max(0, ((obligation.EstimatedDays - daysUntilDue) / obligation.EstimatedDays) * 100)
      : 0;

  let score = 0;
  let trend = "stable";

  // Risk from deadline proximity (0-40 points)
  if (daysUntilDue <= 0) score += 40;
  else if (daysUntilDue <= 3) score += 35;
  else if (daysUntilDue <= 7) score += 25;
  else if (daysUntilDue <= 14) score += 15;
  else if (daysUntilDue <= 30) score += 5;

  // Risk from progress lag (0-35 points)
  const progressLag = expectedProgress - progress;
  if (progressLag > 40) score += 35;
  else if (progressLag > 20) score += 25;
  else if (progressLag > 10) score += 15;
  else if (progressLag > 0) score += 5;

  // Risk level multiplier (0-25 points)
  const riskMultiplier = {
    Low: 0,
    Medium: 8,
    High: 15,
    Critical: 25,
  };
  score += riskMultiplier[obligation.RiskLevel] || 0;

  // Determine trend
  if (score >= 70) trend = "critical";
  else if (score >= 50) trend = "high";
  else if (score >= 30) trend = "medium";
  else trend = "low";

  return {
    score: Math.min(100, score),
    trend,
    recommendation:
      score >= 70
        ? "Immediate escalation required"
        : score >= 50
        ? "Requires urgent attention"
        : score >= 30
        ? "Monitor closely"
        : "On track",
  };
};

// Monitor all obligations and detect issues
export const monitorObligations = async (service) => {
  try {
    const allObligations = await getAllObligations(service);
    const today = new Date();
    const alerts = {
      overdue: [],
      upcoming: [],
      atRisk: [],
      onTrack: [],
    };

    for (const obligation of allObligations) {
      const dueDate = new Date(obligation.DueDate);
      const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

      // Overdue check
      if (daysUntilDue < 0 && obligation.Status !== "Completed" && obligation.Status !== "Waived") {
        alerts.overdue.push({
          ...obligation,
          daysOverdue: Math.abs(daysUntilDue),
        });
        // Auto-mark as overdue if not already
        if (obligation.Status !== "Overdue") {
          await updateObligation(service, obligation.id, { Status: "Overdue" }).catch(() => {});
        }
      }
      // Upcoming check (within reminder days)
      else if (daysUntilDue > 0 && daysUntilDue <= obligation.ReminderDays && obligation.RemindersEnabled) {
        alerts.upcoming.push({
          ...obligation,
          daysRemaining: daysUntilDue,
        });
      }
      // At-risk check
      else if (obligation.Status !== "Completed" && obligation.Status !== "Waived") {
        const riskScore = calculateRiskScore(obligation);
        if (riskScore.score >= 50) {
          alerts.atRisk.push({
            ...obligation,
            riskScore,
          });
          // Auto-mark as at-risk if progress is lagging
          if (obligation.Status !== "At Risk" && riskScore.score >= 70) {
            await updateObligation(service, obligation.id, { Status: "At Risk" }).catch(() => {});
          }
        } else if (obligation.Status === "Not Started" && daysUntilDue <= 7) {
          alerts.atRisk.push({
            ...obligation,
            riskScore,
          });
        } else {
          alerts.onTrack.push(obligation);
        }
      }
    }

    return alerts;
  } catch (e) {
    console.error("Error monitoring obligations:", e);
    return { overdue: [], upcoming: [], atRisk: [], onTrack: [] };
  }
};

// Build obligation notification email
export const buildObligationEmailHTML = (obligation, type) => {
  // type: "reminder" | "overdue" | "escalation"
  const statusColor =
    obligation.Status === "Overdue"
      ? "#a4262c"
      : obligation.Status === "At Risk"
      ? "#ca5010"
      : "#107c10";

  const urgencyLabel =
    type === "overdue"
      ? "⚠️ OVERDUE"
      : type === "escalation"
      ? "🔴 ESCALATION"
      : "📋 Reminder";

  const riskScore = calculateRiskScore(obligation);

  return `
    <div style="font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: linear-gradient(90deg, ${statusColor}, transparent); padding: 1px 0; margin-bottom: 24px;"></div>

      <div style="padding: 0 20px;">
        <div style="font-size: 28px; font-weight: 600; color: #0a1628; margin-bottom: 8px;">
          Obligation ${urgencyLabel}
        </div>
        <p style="color: #666; margin: 0; font-size: 14px;">
          ${obligation.Title}
        </p>
      </div>

      <div style="background: #f5f5f5; margin: 24px 0; padding: 16px 20px; border-left: 4px solid ${statusColor};">
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333; width: 140px;">Obligation:</td>
            <td style="padding: 8px 0; color: #333; font-weight: 500;">${obligation.Title}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Category:</td>
            <td style="padding: 8px 0; color: #666;">${obligation.Category}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Responsible:</td>
            <td style="padding: 8px 0; color: #666;">${obligation.ResponsibleParty}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Due Date:</td>
            <td style="padding: 8px 0; color: #333; font-weight: 600;">${new Date(obligation.DueDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Progress:</td>
            <td style="padding: 8px 0; color: #333;">
              <div style="background: #ddd; height: 20px; border-radius: 4px; overflow: hidden;">
                <div style="background: ${statusColor}; height: 100%; width: ${obligation.ProgressPercentage}%; transition: width 0.3s;"></div>
              </div>
              <div style="font-size: 12px; margin-top: 4px;">${obligation.ProgressPercentage}% complete</div>
            </td>
          </tr>
          <tr>
            <td style="padding: 8px 0; font-weight: 600; color: #333;">Risk Level:</td>
            <td style="padding: 8px 0; color: ${statusColor}; font-weight: 600;">${riskScore.trend.toUpperCase()} (${riskScore.score}/100)</td>
          </tr>
        </table>
      </div>

      ${obligation.RiskDescription ? `
        <div style="background: #fff3cd; border-left: 4px solid #ca5010; padding: 12px 16px; margin: 16px 0; border-radius: 4px;">
          <div style="font-weight: 600; color: #ca5010; margin-bottom: 6px;">⚠️ Risk Assessment</div>
          <div style="color: #333; font-size: 13px;">${obligation.RiskDescription}</div>
        </div>
      ` : ""}

      ${obligation.StakesDescription ? `
        <div style="background: #f0f0f0; border-left: 4px solid #0078d4; padding: 12px 16px; margin: 16px 0; border-radius: 4px;">
          <div style="font-weight: 600; color: #0078d4; margin-bottom: 6px;">📌 What's at Stake</div>
          <div style="color: #333; font-size: 13px;">${obligation.StakesDescription}</div>
        </div>
      ` : ""}

      <div style="text-align: center; margin: 24px 0;">
        <a href="${typeof window !== "undefined" ? window.location.origin : ""}/contracts/${obligation.ContractID}" style="display: inline-block; background: ${statusColor}; color: white; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 14px;">
          View & Update Obligation
        </a>
      </div>

      <hr style="border: none; border-top: 1px solid #ddd; margin: 24px 0;">
      <p style="font-size: 12px; color: #999; text-align: center; margin: 0;">
        Automated message from LexCore Contract Management System.
      </p>
    </div>
  `;
};

// Helper function to safely parse JSON
function tryParseJson(str, defaultValue) {
  try {
    return str ? JSON.parse(str) : defaultValue;
  } catch {
    return defaultValue;
  }
}

// Delete obligation
export const deleteObligation = async (service, obligationId) => {
  try {
    await service.deleteListItem("ContractObligations", obligationId);
    return true;
  } catch (e) {
    console.error("Error deleting obligation:", e);
    throw e;
  }
};
